import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Tooltip from './common/Tooltip';
import { useQuery } from '@apollo/client';
import Cookies from 'js-cookie';
import PositionRow, { PositionMobileCard } from './PositionRow';
import PositionFilterModal from './PositionFilterModal';
import { GET_FILTERED_POSITIONS } from '../graphql/queries/filteredPositions';
import { GET_TOKEN_LOCATIONS } from '../graphql/queries/tokenLocations';
import { GET_TOKEN_GROUPS } from '../graphql/queries/tokenGroups';
import { parseFloatFromString, parseReadableNumberToFloat } from '../utils/formatters';
import useStockLocations from '../hooks/useStockLocations';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { GET_ALL_ALLOCATION_PLANS } from '../graphql/queries/allocationPlans';
import EmptyState from './EmptyState';
import { DUST_THRESHOLD_USD } from '../constants/dust';
import { GET_FILTERED_STOCK_POSITIONS } from '../graphql/queries/stocks';

// Pagination limit options (default 100)
const PAGE_SIZE_OPTIONS = [50, 100, 250];

const MOBILE_SORT_OPTIONS = [
  { value: 'holdingsValue', label: 'Holdings' },
  { value: 'currentPnl', label: 'PnL $' },
  { value: 'pnlPercentage', label: 'ROI %' },
  { value: 'riskScore', label: 'Risk' },
  { value: 'token.marketData.priceChange24h', label: '24h' },
  { value: 'token.marketData.priceChange7d', label: '7d' },
  { value: 'token.marketData.volume24h', label: 'Volume' },
  { value: 'token.marketData.marketCap', label: 'MC' },
  { value: 'token.name', label: 'Name' },
  { value: 'strategyDelta', label: 'Strategy' }
];

const STOCK_MOBILE_SORT_OPTIONS = [
  { value: 'holdingsValue', label: 'Holdings' },
  { value: 'currentPnl', label: 'PnL $' },
  { value: 'pnlPercentage', label: 'ROI %' },
  { value: 'token.marketData.priceChange24h', label: 'Day' },
  { value: 'token.marketData.priceChange7d', label: '7d' },
  { value: 'token.name', label: 'Name' }
];

// Default filter state
const DEFAULT_FILTER_STATE = {
  selectedGroup: null,
  selectedLocation: null,
  selectedMarketCap: null,
  selectedSector: null,
  selectedInstrumentType: null,
  sortBy: 'holdingsValue',
  sortDir: 'desc',
  groupedView: false,
  hideDust: false,
  searchTerm: '',
  exitStrategyFilter: '',
  invalidationFilter: '',
  selectedTags: [],
  tagFilterMode: 'ANY',
  pageSize: 100
};

// LocalStorage key for filter state
const FILTER_STATE_KEY = 'positionListFilters';
const STOCK_FILTER_STATE_KEY = 'stockPositionListFilters';

// Debounce delay for search input (ms)
const SEARCH_DEBOUNCE_MS = 300;

// Map frontend sort field to API enum (all sorting is done on the backend)
const sortByToApi = (sortBy) => {
  const map = {
    'token.name': 'TOKEN_NAME',
    'holdingsValue': 'HOLDINGS_VALUE',
    'token.marketData.marketCap': 'MARKET_CAP',
    'currentPrice': 'CURRENT_PRICE',
    'token.marketData.priceChange1h': 'PRICE_CHANGE_1H',
    'token.marketData.priceChange24h': 'PRICE_CHANGE_24H',
    'token.marketData.priceChange7d': 'PRICE_CHANGE_7D',
    'token.marketData.priceChange30d': 'PRICE_CHANGE_30D',
    'token.marketData.priceChange1y': 'PRICE_CHANGE_1Y',
    'token.marketData.volume24h': 'VOLUME_24H',
    'percentage': 'HOLDINGS_VALUE',
    'groupPercentage': 'HOLDINGS_VALUE',
    'currentPnl': 'CURRENT_PNL',
    'pnlPercentage': 'CURRENT_ROI',
    'strategyDelta': 'STRATEGY_DELTA',
    'riskScore': 'RISK_SCORE'
  };
  return map[sortBy] || 'HOLDINGS_VALUE';
};

const stockSortByToApi = (sortBy) => {
  const field = sortByToApi(sortBy);
  // Ignore legacy/crypto-only saved sorts; the stock API has its own enum.
  return [
    'TOKEN_NAME', 'HOLDINGS_VALUE', 'CURRENT_PNL', 'CURRENT_ROI', 'CURRENT_PRICE',
    'PRICE_CHANGE_24H', 'PRICE_CHANGE_7D', 'PRICE_CHANGE_30D', 'PRICE_CHANGE_1Y',
  ].includes(field) ? field : 'HOLDINGS_VALUE';
};

const PositionList = ({ navState, assetKind = 'crypto' }) => {
  const isStock = assetKind === 'stock';
  const filterStateKey = isStock ? STOCK_FILTER_STATE_KEY : FILTER_STATE_KEY;
  const { currentPortfolioId } = usePortfolioContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedGroup, setSelectedGroup] = useState(DEFAULT_FILTER_STATE.selectedGroup);
  const [selectedLocation, setSelectedLocation] = useState(DEFAULT_FILTER_STATE.selectedLocation);
  const [sortBy, setSortBy] = useState(DEFAULT_FILTER_STATE.sortBy);
  const [sortDir, setSortDir] = useState(DEFAULT_FILTER_STATE.sortDir);
  const [groupedView, setGroupedView] = useState(DEFAULT_FILTER_STATE.groupedView);
  const [hideDust, setHideDust] = useState(DEFAULT_FILTER_STATE.hideDust);
  const [searchTerm, setSearchTerm] = useState(DEFAULT_FILTER_STATE.searchTerm);
  const [selectedMarketCap, setSelectedMarketCap] = useState(DEFAULT_FILTER_STATE.selectedMarketCap);
  const [selectedSector, setSelectedSector] = useState(DEFAULT_FILTER_STATE.selectedSector);
  const [selectedInstrumentType, setSelectedInstrumentType] = useState(DEFAULT_FILTER_STATE.selectedInstrumentType);
  const [exitStrategyFilter, setExitStrategyFilter] = useState(DEFAULT_FILTER_STATE.exitStrategyFilter);
  const [invalidationFilter, setInvalidationFilter] = useState(DEFAULT_FILTER_STATE.invalidationFilter);
  const [selectedTags, setSelectedTags] = useState(DEFAULT_FILTER_STATE.selectedTags);
  const [tagFilterMode, setTagFilterMode] = useState(DEFAULT_FILTER_STATE.tagFilterMode);
  const [pageSize, setPageSize] = useState(DEFAULT_FILTER_STATE.pageSize);
  const [currentPage, setCurrentPage] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(DEFAULT_FILTER_STATE.searchTerm);
  const [isInitialized, setIsInitialized] = useState(false);
  const [pendingGroupName, setPendingGroupName] = useState(null);
  const [pendingLocationName, setPendingLocationName] = useState(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [expandedMobilePositionId, setExpandedMobilePositionId] = useState(null);
  const prevPortfolioIdRef = useRef(currentPortfolioId);

  const { data: locationsData } = useQuery(GET_TOKEN_LOCATIONS, {
    variables: { portfolioId: currentPortfolioId },
    skip: !currentPortfolioId || isStock
  });
  const { locations: stockLocations } = useStockLocations(currentPortfolioId, { skip: !isStock });
  const locationOptions = useMemo(
    () => (isStock ? stockLocations : (locationsData?.tokenLocations || [])),
    [isStock, stockLocations, locationsData]
  );
  const { data: groupsData } = useQuery(GET_TOKEN_GROUPS, {
    variables: { portfolioId: currentPortfolioId },
    skip: !currentPortfolioId || isStock
  });

  // Build filter for backend (group/location use "none" -> API maps to ungrouped/unlocated)
  const filterInput = useMemo(() => {
    let marketCapCategory = null;
    if (selectedMarketCap === 'large') marketCapCategory = 'LARGE_CAP';
    else if (selectedMarketCap === 'mid') marketCapCategory = 'MID_CAP';
    else if (selectedMarketCap === 'low') marketCapCategory = 'LOW_CAP';

    const filter = {
      portfolioId: currentPortfolioId,
      groupId: selectedGroup || null,
      locationId: selectedLocation || null,
      marketCapCategory,
      searchQuery: debouncedSearchTerm?.trim() || null,
      excludeDust: hideDust || null,
      limit: pageSize,
      offset: currentPage * pageSize,
      sortBy: sortByToApi(sortBy),
      sortDir: sortDir === 'asc' ? 'ASC' : 'DESC'
    };

    if (exitStrategyFilter === 'unset') filter.hasExitStrategy = false;
    else if (exitStrategyFilter === 'set') filter.hasExitStrategy = true;
    else if (exitStrategyFilter === 'triggered') { filter.hasExitStrategy = true; filter.exitStrategyStatus = 'TRIGGERED'; }
    else if (exitStrategyFilter === 'approaching') { filter.hasExitStrategy = true; filter.exitStrategyStatus = 'APPROACHING'; }

    if (invalidationFilter === 'unset') filter.hasInvalidationStrategy = false;
    else if (invalidationFilter === 'set') filter.hasInvalidationStrategy = true;
    else if (invalidationFilter === 'triggered') { filter.hasInvalidationStrategy = true; filter.invalidationStatus = 'TRIGGERED'; }
    else if (invalidationFilter === 'approaching') { filter.hasInvalidationStrategy = true; filter.invalidationStatus = 'APPROACHING'; }

    if (selectedTags.length > 0) {
      filter.tagIds = selectedTags;
      filter.tagFilterMode = tagFilterMode || 'ANY';
    }

    return filter;
  }, [currentPortfolioId, selectedGroup, selectedLocation, selectedMarketCap, debouncedSearchTerm, hideDust, exitStrategyFilter, invalidationFilter, selectedTags, tagFilterMode, pageSize, currentPage, sortBy, sortDir]);

  const cryptoQuery = useQuery(GET_FILTERED_POSITIONS, {
    variables: { filter: filterInput },
    skip: !currentPortfolioId || isStock,
    fetchPolicy: 'cache-and-network'
  });
  const stockFilterInput = useMemo(() => ({
    portfolioId: currentPortfolioId,
    sector: selectedSector || null,
    instrumentType: selectedInstrumentType || null,
    locationId: selectedLocation || null,
    searchQuery: debouncedSearchTerm?.trim() || null,
    excludeDust: hideDust || null,
    tagIds: selectedTags.length > 0 ? selectedTags.map((id) => id === 'none' ? 'untagged' : id) : null,
    tagFilterMode: tagFilterMode || 'ANY',
    limit: pageSize,
    offset: currentPage * pageSize,
    sortBy: stockSortByToApi(sortBy),
    sortDir: sortDir === 'asc' ? 'ASC' : 'DESC',
  }), [currentPortfolioId, selectedSector, selectedInstrumentType, selectedLocation, debouncedSearchTerm,
    hideDust, selectedTags, tagFilterMode, pageSize, currentPage, sortBy, sortDir]);

  const stockQuery = useQuery(GET_FILTERED_STOCK_POSITIONS, {
    variables: { filter: stockFilterInput },
    skip: !currentPortfolioId || !isStock,
    fetchPolicy: 'cache-and-network'
  });
  const { data: positionsData, loading, error, refetch: refetchPositions } = isStock ? stockQuery : cryptoQuery;

  // The backend supplies portfolio-wide options independently of filters and paging.
  const stockSectorOptions = stockQuery.data?.filteredStockPositions?.filterOptions?.sectors ?? [];
  const stockTypeOptions = stockQuery.data?.filteredStockPositions?.filterOptions?.instrumentTypes ?? [];
  const result = isStock ? positionsData?.filteredStockPositions : positionsData?.filteredPositions;
  const positions = result?.positions ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { data: plansData, refetch: refetchPlans } = useQuery(GET_ALL_ALLOCATION_PLANS, {
    variables: { portfolioId: currentPortfolioId },
    skip: !currentPortfolioId || isStock,
    fetchPolicy: 'cache-first'
  });
  const planOptions = (plansData?.allAllocationPlans || []).map(p => ({ id: p.id, name: p.name }));
  const selectedPlanCookieKey = currentPortfolioId ? `selectedPlanId_${currentPortfolioId}` : 'selectedPlanId';
  const initialPlanId = Cookies.get(selectedPlanCookieKey) || (planOptions[0]?.id || null);

  // Debounce search term for API requests
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset to page 0 when filters or page size change (not when sort changes; use debounced search)
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedGroup, selectedLocation, selectedMarketCap, selectedSector, selectedInstrumentType, debouncedSearchTerm, hideDust, exitStrategyFilter, invalidationFilter, selectedTags, tagFilterMode, pageSize]);

  // Load filter state from localStorage or URL on component mount
  useEffect(() => {
    console.log('Loading filter state from localStorage...'); // Debug log
    try {
      // Check URL params for deep-linked filters
      const params = new URLSearchParams(window.location.search || '');
      const urlMc = params.get('mc'); // market cap bucket: large|mid|low

      const savedState = localStorage.getItem(filterStateKey);
      console.log('Raw saved state:', savedState); // Debug log
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        console.log('Parsed saved state:', parsedState); // Debug log
        // Prefer URL-provided filters when present
        // Apply saved selectedGroup; URL overrides handled transiently in filtering
        setSelectedGroup(parsedState.selectedGroup ?? DEFAULT_FILTER_STATE.selectedGroup);
        setSelectedLocation(parsedState.selectedLocation ?? DEFAULT_FILTER_STATE.selectedLocation);
        setSelectedMarketCap(parsedState.selectedMarketCap ?? DEFAULT_FILTER_STATE.selectedMarketCap);
        setSelectedSector(isStock ? parsedState.selectedSector ?? null : null);
        setSelectedInstrumentType(isStock ? parsedState.selectedInstrumentType ?? null : null);
        setSortBy(parsedState.sortBy ?? DEFAULT_FILTER_STATE.sortBy);
        setSortDir(parsedState.sortDir ?? DEFAULT_FILTER_STATE.sortDir);
        setGroupedView(parsedState.groupedView ?? DEFAULT_FILTER_STATE.groupedView);
        setHideDust(parsedState.hideDust ?? DEFAULT_FILTER_STATE.hideDust);
        const initialSearch = parsedState.searchTerm ?? DEFAULT_FILTER_STATE.searchTerm;
        setSearchTerm(initialSearch);
        setDebouncedSearchTerm(initialSearch);
        setExitStrategyFilter(parsedState.exitStrategyFilter ?? DEFAULT_FILTER_STATE.exitStrategyFilter);
        setInvalidationFilter(parsedState.invalidationFilter ?? DEFAULT_FILTER_STATE.invalidationFilter);
        setSelectedTags(parsedState.selectedTags ?? DEFAULT_FILTER_STATE.selectedTags);
        setTagFilterMode(parsedState.tagFilterMode ?? DEFAULT_FILTER_STATE.tagFilterMode);
        setPageSize(parsedState.pageSize ?? DEFAULT_FILTER_STATE.pageSize);
      } else {
        console.log('No saved state found, using defaults'); // Debug log
        // Do not set selectedGroup from URL to avoid conflicting with existing UI control
      }
      // Store market cap url param for filtering below (one-time intent)
      if (urlMc) {
        try {
          setSelectedMarketCap(urlMc);
          console.log('URL mc param detected, applying selectedMarketCap:', urlMc);
        } catch (_) {}
      }

      // Legacy localStorage intents only apply when the router has no explicit intent.
      try {
        if (!navState || typeof navState !== 'object') {
          const rawIntent = localStorage.getItem('POSITION_FILTER_INTENT');
          if (rawIntent) {
            const intent = JSON.parse(rawIntent);
            if (intent && typeof intent === 'object') {
              if (intent.selectedMarketCap) {
                setSelectedMarketCap(intent.selectedMarketCap);
                console.log('Applied intent.selectedMarketCap:', intent.selectedMarketCap);
              }
              if (intent.resetGroup) {
                setSelectedGroup(null);
                console.log('Applied intent.resetGroup');
              }
              if (intent.resetLocation) {
                setSelectedLocation(null);
                console.log('Applied intent.resetLocation');
              }
            }
            localStorage.removeItem('POSITION_FILTER_INTENT');
          }
          const rawGroupName = localStorage.getItem('POSITION_FILTER_GROUP_NAME_INTENT');
          if (rawGroupName) {
            setPendingGroupName(rawGroupName);
            console.log('Pending group name intent detected:', rawGroupName);
            localStorage.removeItem('POSITION_FILTER_GROUP_NAME_INTENT');
          }
        }
      } catch (_) {}
    } catch (error) {
      console.error('Error loading filter state from localStorage:', error);
      // If there's an error, we'll just use the default state
    } finally {
      // Mark as initialized after loading is complete
      setIsInitialized(true);
      console.log('Filter state initialization complete'); // Debug log
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply fresh router intents even on same-route navigation. Clearing state
  // below consumes this entry's intent without reinitializing the filters.
  useEffect(() => {
    if (!navState || typeof navState !== 'object') return;
    // Reset all filters so one breakdown doesn't leave another's filter active
    setSelectedGroup(DEFAULT_FILTER_STATE.selectedGroup);
    setSelectedLocation(DEFAULT_FILTER_STATE.selectedLocation);
    setSelectedMarketCap(DEFAULT_FILTER_STATE.selectedMarketCap);
    setSelectedSector(DEFAULT_FILTER_STATE.selectedSector);
    setSelectedInstrumentType(DEFAULT_FILTER_STATE.selectedInstrumentType);
    setExitStrategyFilter(DEFAULT_FILTER_STATE.exitStrategyFilter);
    setInvalidationFilter(DEFAULT_FILTER_STATE.invalidationFilter);
    setHideDust(DEFAULT_FILTER_STATE.hideDust);
    setSelectedTags(DEFAULT_FILTER_STATE.selectedTags);
    setTagFilterMode(DEFAULT_FILTER_STATE.tagFilterMode);
    setPendingGroupName(null);
    setPendingLocationName(null);

    setSearchTerm(DEFAULT_FILTER_STATE.searchTerm);
    setDebouncedSearchTerm(DEFAULT_FILTER_STATE.searchTerm);
    setCurrentPage(0);
    if (isStock) {
      setSelectedSector(navState.sector ?? null);
      setSelectedInstrumentType(navState.instrumentType ?? null);
    }

    if (navState.mc) {
      setSelectedMarketCap(navState.mc);
    }
    if (navState.groupName !== undefined) {
      const normalized = String(navState.groupName || '').trim().toLowerCase();
      if (normalized === 'ungrouped' || normalized === 'ungroupped' || normalized === 'none') {
        setSelectedGroup('none');
        setPendingGroupName(null);
      } else {
        setPendingGroupName(navState.groupName);
      }
    }
    if (navState.locationName !== undefined) {
      const normalized = String(navState.locationName || '').trim().toLowerCase();
      if (normalized === 'unlocated' || normalized === 'unassigned' || normalized === 'none') {
        setSelectedLocation('none');
        setPendingLocationName(null);
      } else {
        setPendingLocationName(navState.locationName);
      }
    }
    if (navState.withoutExitStrategy) {
      setExitStrategyFilter('unset');
    }
    if (navState.withoutInvalidationStrategy) {
      setInvalidationFilter('unset');
    }
    if (navState.exitStrategyFilter != null && navState.exitStrategyFilter !== '') {
      setExitStrategyFilter(navState.exitStrategyFilter);
    }
    if (navState.invalidationFilter != null && navState.invalidationFilter !== '') {
      setInvalidationFilter(navState.invalidationFilter);
    }
    // Replace only the current entry, preserving its URL and the applied filters.
    // Reloads and history returns must not replay this dashboard intent.
    navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, {
      replace: true,
      state: null,
    });
  }, [navState, isStock, location.key, location.pathname, location.search, location.hash, navigate]);

  // Determine if a group filter is applied (either via selectedGroup or URL param groupName)
  const isUrlGroupIntent = (() => {
    try {
      const params = new URLSearchParams(location?.search || '');
      return Boolean(params.get('groupName'));
    } catch (_) { return false; }
  })();
  const isGroupFilterApplied = Boolean(selectedGroup) || isUrlGroupIntent;

  // Force Group View off whenever a group filter is active
  useEffect(() => {
    if (isGroupFilterApplied && groupedView) {
      setGroupedView(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroupFilterApplied, groupedView]);

  // Resolve pending group name to ID when groups list is available
  useEffect(() => {
    if (!pendingGroupName) return;
    const list = groupsData?.tokenGroups || [];
    const match = list.find((g) => (g?.name || '') === pendingGroupName);
    if (match?.id) {
      setSelectedGroup(match.id);
      setPendingGroupName(null);
    }
  }, [pendingGroupName, groupsData]);

  // Resolve pending location name to ID when locations list is available
  useEffect(() => {
    if (!pendingLocationName) return;
    const match = locationOptions.find((l) => (l?.name || '') === pendingLocationName);
    if (match?.id) {
      setSelectedLocation(match.id);
      setPendingLocationName(null);
    }
  }, [pendingLocationName, locationOptions]);

  // Reset position filters when portfolio changes (groups/locations are portfolio-specific)
  useEffect(() => {
    if (prevPortfolioIdRef.current !== currentPortfolioId && prevPortfolioIdRef.current != null) {
      setSelectedGroup(DEFAULT_FILTER_STATE.selectedGroup);
      setSelectedLocation(DEFAULT_FILTER_STATE.selectedLocation);
      setSelectedMarketCap(DEFAULT_FILTER_STATE.selectedMarketCap);
      setSelectedSector(DEFAULT_FILTER_STATE.selectedSector);
      setSelectedInstrumentType(DEFAULT_FILTER_STATE.selectedInstrumentType);
      setSortBy(DEFAULT_FILTER_STATE.sortBy);
      setSortDir(DEFAULT_FILTER_STATE.sortDir);
      setGroupedView(DEFAULT_FILTER_STATE.groupedView);
      setHideDust(DEFAULT_FILTER_STATE.hideDust);
      setSearchTerm(DEFAULT_FILTER_STATE.searchTerm);
      setDebouncedSearchTerm(DEFAULT_FILTER_STATE.searchTerm);
      setExitStrategyFilter(DEFAULT_FILTER_STATE.exitStrategyFilter);
      setInvalidationFilter(DEFAULT_FILTER_STATE.invalidationFilter);
      setSelectedTags(DEFAULT_FILTER_STATE.selectedTags);
      setTagFilterMode(DEFAULT_FILTER_STATE.tagFilterMode);
      setPageSize(DEFAULT_FILTER_STATE.pageSize);
      setCurrentPage(0);
      setPendingGroupName(null);
      setPendingLocationName(null);
      try {
        localStorage.removeItem(filterStateKey);
      } catch (_) {}
    }
    prevPortfolioIdRef.current = currentPortfolioId;
  }, [currentPortfolioId, filterStateKey]);

  // Save filter state to localStorage whenever it changes (but only after initialization)
  useEffect(() => {
    if (!isInitialized) return; // Don't save during initial load
    
    try {
      const stateToSave = {
        selectedGroup,
        selectedLocation,
        selectedMarketCap,
        ...(isStock ? { selectedSector, selectedInstrumentType } : {}),
        sortBy,
        sortDir,
        groupedView,
        hideDust,
        searchTerm,
        exitStrategyFilter,
        invalidationFilter,
        selectedTags,
        tagFilterMode,
        pageSize
      };
      localStorage.setItem(filterStateKey, JSON.stringify(stateToSave));
      console.log('Saved filter state:', stateToSave); // Debug log
    } catch (error) {
      console.error('Error saving filter state to localStorage:', error);
    }
  }, [selectedGroup, selectedLocation, selectedMarketCap, selectedSector, selectedInstrumentType, isStock, sortBy, sortDir, groupedView, hideDust, searchTerm, exitStrategyFilter, invalidationFilter, selectedTags, tagFilterMode, pageSize, isInitialized, filterStateKey]);

  // Reset all filters to default state
  const resetFilters = () => {
    setSelectedGroup(DEFAULT_FILTER_STATE.selectedGroup);
    setSelectedLocation(DEFAULT_FILTER_STATE.selectedLocation);
    setSelectedMarketCap(DEFAULT_FILTER_STATE.selectedMarketCap);
    setSelectedSector(DEFAULT_FILTER_STATE.selectedSector);
    setSelectedInstrumentType(DEFAULT_FILTER_STATE.selectedInstrumentType);
    setSortBy(DEFAULT_FILTER_STATE.sortBy);
    setSortDir(DEFAULT_FILTER_STATE.sortDir);
    setGroupedView(DEFAULT_FILTER_STATE.groupedView);
    setHideDust(DEFAULT_FILTER_STATE.hideDust);
    setSearchTerm(DEFAULT_FILTER_STATE.searchTerm);
    setDebouncedSearchTerm(DEFAULT_FILTER_STATE.searchTerm);
    setExitStrategyFilter(DEFAULT_FILTER_STATE.exitStrategyFilter);
    setInvalidationFilter(DEFAULT_FILTER_STATE.invalidationFilter);
    setSelectedTags(DEFAULT_FILTER_STATE.selectedTags);
    setTagFilterMode(DEFAULT_FILTER_STATE.tagFilterMode);
    setPageSize(DEFAULT_FILTER_STATE.pageSize);
    setCurrentPage(0);
  };

  // Debug function to check current localStorage state
  const checkLocalStorage = () => {
    const savedState = localStorage.getItem(filterStateKey);
    console.log('Current localStorage state:', savedState);
    return savedState;
  };

  // Add debug info to window for testing
  if (typeof window !== 'undefined') {
    window.checkPositionFilters = checkLocalStorage;
  }

  const handleRefetch = () => {
    try {
      refetchPositions();
    } catch (err) {
      console.error("Error reloading positions:", err);
    }
  };

  const hasNoPositions = !loading && (totalCount === 0 || !positions?.length);

  // Calculate portfolio percentage of each position (for current page)
  const calculatePortfolioPercentages = (positionsToCalculate) => {
    const parsedPositions = positionsToCalculate.map(pos => ({
      ...pos,
      numericValue: parseFloatFromString(pos.holdingsValue)
    }));
    const totalValue = parsedPositions.reduce((sum, pos) => sum + pos.numericValue, 0);
    return parsedPositions.map(pos => {
      const percentage = totalValue > 0 ? (pos.numericValue / totalValue) * 100 : 0;
      return {
        ...pos,
        percentage: percentage.toFixed(2) + '%',
        cumulativePercentage: '0.00%'
      };
    });
  };

  const positionsWithPercentages = calculatePortfolioPercentages(positions);

  // Backend returns positions already sorted; we only compute percentages/cumulative in that order.
  // sortPositions is still used for grouped view (sorting within each group) and as fallback for display order.
  const sortPositions = (positions, sortField = sortBy, sortDirection = sortDir) => {
    return [...positions].sort((a, b) => {
      // Custom sort for strategy/levels column: match Exits page logic
      if (sortField === 'strategyDelta') {
        const aHas = Array.isArray(a.exitStrategy?.levels) && a.exitStrategy.levels.length > 0;
        const bHas = Array.isArray(b.exitStrategy?.levels) && b.exitStrategy.levels.length > 0;

        // Helper: first non-filled level delta
        const firstNonFilledDelta = (pos) => {
          const currentPrice = parseFloatFromString(pos.token?.marketData?.currentPrice || '0');
          const levels = [...(pos.exitStrategy?.levels || [])].sort((l1, l2) => parseFloatFromString(l1.price) - parseFloatFromString(l2.price));
          const target = levels.find(l => (l.status || 'PLANNED').toUpperCase() !== 'FILLED');
          if (!target || currentPrice <= 0) return Number.POSITIVE_INFINITY;
          const lp = parseFloatFromString(target.price);
          return ((lp / currentPrice) - 1) * 100;
        };

        if (aHas && bHas) {
          const aDelta = firstNonFilledDelta(a);
          const bDelta = firstNonFilledDelta(b);
          return sortDirection === 'asc' ? (aDelta - bDelta) : (bDelta - aDelta);
        }

        if (!aHas && !bHas) {
          const aAmt = parseFloatFromString(a.holdingsAmount || '0');
          const bAmt = parseFloatFromString(b.holdingsAmount || '0');
          return sortDirection === 'asc' ? (aAmt - bAmt) : (bAmt - aAmt);
        }

        // Prefer positions WITH strategies when sorting by strategy column
        return aHas ? -1 : 1;
      }

      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle special cases for nested properties
      if (sortField === 'token.name') {
        aValue = a.token.name;
        bValue = b.token.name;
      } else if (sortField === 'currentPrice') {
        aValue = parseFloatFromString(a.token.marketData?.currentPrice || '0');
        bValue = parseFloatFromString(b.token.marketData?.currentPrice || '0');
      } else if (sortField === 'percentage') {
        aValue = parseFloatFromString(a.percentage);
        bValue = parseFloatFromString(b.percentage);
      } else if (sortField === 'groupPercentage') {
        aValue = parseFloatFromString(a.groupPercentage || a.percentage || '0');
        bValue = parseFloatFromString(b.groupPercentage || b.percentage || '0');
      } else if (sortField === 'currentPnl') {
        aValue = parseFloatFromString(a.current?.pnl || '0');
        bValue = parseFloatFromString(b.current?.pnl || '0');
      } else if (sortField === 'pnlPercentage') {
        aValue = parseFloatFromString(a.current?.roi || '0');
        bValue = parseFloatFromString(b.current?.roi || '0');
      } else if (sortField === 'holdingsValue') {
        aValue = a.numericValue || 0; // Use the pre-calculated numeric value
        bValue = b.numericValue || 0;
      } else if (sortField.startsWith('token.marketData.priceChange')) {
        // Handle price change sorting (1h, 24h, 7d, 30d, 1y)
        aValue = parseFloatFromString(a.token.marketData?.[sortField.split('.')[2]] || '0');
        bValue = parseFloatFromString(b.token.marketData?.[sortField.split('.')[2]] || '0');
      } else if (sortField === 'token.marketData.volume24h') {
        aValue = parseReadableNumberToFloat(a.token.marketData?.volume24h || '0');
        bValue = parseReadableNumberToFloat(b.token.marketData?.volume24h || '0');
      } else if (sortField === 'token.marketData.marketCap') {
        const aMc = parseReadableNumberToFloat(a.token.marketData?.marketCap || '0');
        const bMc = parseReadableNumberToFloat(b.token.marketData?.marketCap || '0');
        const aFdv = parseReadableNumberToFloat(a.token.marketData?.fullyDilutedValuation || '0');
        const bFdv = parseReadableNumberToFloat(b.token.marketData?.fullyDilutedValuation || '0');
        aValue = aMc > 0 ? aMc : (aFdv > 0 ? aFdv : 0);
        bValue = bMc > 0 ? bMc : (bFdv > 0 ? bFdv : 0);
      } else if (sortField === 'currentRoi') {
        aValue = parseFloatFromString(a.current?.roi || '0');
        bValue = parseFloatFromString(b.current?.roi || '0');
      } else if (sortField === 'plannedRoi') {
        aValue = parseFloatFromString(a.planned?.roi || '0');
        bValue = parseFloatFromString(b.planned?.roi || '0');
      }

      // Convert percentage strings to numbers
      if (typeof aValue === 'string' && aValue.includes('%')) {
        aValue = parseFloatFromString(aValue);
        bValue = parseFloatFromString(bValue);
      }

      // Basic comparison
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Sort positions
  // Use API order (backend sorts before pagination); no client-side re-sort
  const sortedPositions = positionsWithPercentages;

  // Recompute percentage and cumulativePercentage from numeric values in the current sorted order
  const sortedPositionsWithCumulative = (() => {
    // Calculate total value for percentage calculations
    const totalValue = sortedPositions.reduce((sum, pos) => {
      const value = pos.numericValue ?? parseFloatFromString(pos.holdingsValue || '0');
      return sum + (isNaN(value) ? 0 : value);
    }, 0);

    let cumulativePercentage = 0;
    return sortedPositions.map((position, index) => {
      const itemValue = position.numericValue ?? parseFloatFromString(position.holdingsValue || '0');
      const safeItemValue = isNaN(itemValue) ? 0 : itemValue;

      const sizePercent = totalValue > 0 ? (safeItemValue / totalValue) * 100 : 0;
      cumulativePercentage += sizePercent;

      // Ensure the last item shows exactly 100% cumulative
      const finalCumulative = index === sortedPositions.length - 1 ? 100 : cumulativePercentage;

      return {
        ...position,
        percentage: `${sizePercent.toFixed(2)}%`,
        cumulativePercentage: `${finalCumulative.toFixed(2)}%`
      };
    });
  })();
  
  // Handle sorting headers
  const handleSort = (field) => {
    if (field === sortBy) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      // For strategy column, default to ascending (more negative deltas first)
      setSortDir(field === 'strategyDelta' ? 'asc' : 'desc');
    }
  };

  const handleMobileSortByChange = (field) => {
    setSortBy(field);
    setSortDir(field === 'strategyDelta' ? 'asc' : 'desc');
  };
  
  // Get sort indicator
  const getSortIndicator = (field) => {
    if (field !== sortBy) return null;
    return sortDir === 'asc' ? '↑' : '↓';
  };

  // Group positions by token group for grouped view
  const getGroupedPositions = () => {
    if (isStock || !groupedView) return { positions: sortedPositions, groups: [] };

    // Calculate total portfolio value
    const totalPortfolioValue = positionsWithPercentages.reduce(
      (sum, pos) => sum + (pos.numericValue || 0), 
      0
    );

    // Create a map to store positions by group
    const groupMap = new Map();
    
    // Add an "Ungrouped" category for positions without a group
    groupMap.set('ungrouped', { 
      id: 'ungrouped',
      name: 'Ungrouped',
      positions: [],
      totalValue: 0
    });
    
    // Initialize groups from groupsData (safely)
    if (groupsData?.tokenGroups && Array.isArray(groupsData.tokenGroups)) {
      groupsData.tokenGroups.forEach(group => {
        groupMap.set(group.id, { 
          ...group, 
          positions: [],
          totalValue: 0
        });
      });
    }
    
    // Distribute positions to their respective groups
    sortedPositionsWithCumulative.forEach(position => {
      const groupId = position.token.group ? position.token.group.id : 'ungrouped';
      const group = groupMap.get(groupId);
      
      if (group) {
        group.positions.push(position);
        group.totalValue += position.numericValue || 0;
      }
    });
    
    // Process each group to calculate percentages within the group
    const groups = Array.from(groupMap.values())
      .filter(group => group.positions.length > 0)
      .map(group => {
        // Calculate percentages within this group
        let groupCumulativePercentage = 0;
        
        // Sort positions within the group according to current sort criteria
        const sortedGroupPositions = sortPositions(group.positions);

        const positionsWithGroupPercentages = sortedGroupPositions.map((position, index) => {
          const groupPercentage = group.totalValue > 0 
            ? ((position.numericValue || 0) / group.totalValue) * 100
            : 0;

          groupCumulativePercentage += groupPercentage;
          const isLastInGroup = index === sortedGroupPositions.length - 1;
          const cumulative = isLastInGroup ? 100 : groupCumulativePercentage;
          
          return {
            ...position,
            groupPercentage: `${groupPercentage.toFixed(2)}%`,
            groupCumulativePercentage: `${cumulative.toFixed(2)}%`
          };
        });

        // Calculate this group's percentage of the whole portfolio
        const portfolioPercentage = totalPortfolioValue > 0
          ? ((group.totalValue / totalPortfolioValue) * 100).toFixed(2) + '%'
          : '0.00%';

        return {
          ...group,
          positions: positionsWithGroupPercentages,
          numericTotalValue: group.totalValue, // Keep the numeric value for sorting
          totalValue: new Intl.NumberFormat('en-US', { 
            style: 'currency', 
            currency: (localStorage.getItem('selectedPortfolioCurrency') || 'USD') 
          }).format(group.totalValue),
          totalPercentage: '100.00%',  // Each group internally represents 100% 
          portfolioPercentage: portfolioPercentage  // Group's percentage of whole portfolio
        };
      })
      // Sort groups by their total value in descending order
      .sort((a, b) => b.numericTotalValue - a.numericTotalValue);
    
    return { positions: sortedPositionsWithCumulative, groups };
  };

  const { groups } = getGroupedPositions();
  const tokenLocations = locationOptions;

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const isFiltered = Boolean(
    (searchTerm && searchTerm.trim() !== '') ||
    selectedGroup ||
    selectedLocation ||
    selectedMarketCap ||
    (isStock && (selectedSector || selectedInstrumentType)) ||
    exitStrategyFilter ||
    invalidationFilter ||
    (selectedTags && selectedTags.length > 0) ||
    hideDust ||
    isUrlGroupIntent
  );

  // Render positions as mobile cards and as a full desktop table.
  const renderPositionTable = (positionsToRender, groupHeader = null) => (
    <div className={`bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-visible ${groupHeader ? 'mb-6' : ''}`}>
      {groupHeader && (
        <div className="px-4 py-3 bg-gray-100 dark:bg-gray-700 flex justify-between items-center">
          <h3 className="font-semibold text-gray-800 dark:text-gray-200 truncate">{groupHeader.name}</h3>
          <div className="flex shrink-0 gap-3 text-sm">
            <span className="text-gray-700 dark:text-gray-300">{groupHeader.totalValue}</span>
            <span className="text-gray-700 dark:text-gray-300">{groupHeader.portfolioPercentage}</span>
          </div>
        </div>
      )}
      <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
        {positionsToRender.map((position) => (
          <PositionMobileCard
            key={position.id}
            position={{
              ...position,
              percentage: groupHeader ? position.groupPercentage : position.percentage,
              cumulativePercentage: groupHeader ? position.groupCumulativePercentage : position.cumulativePercentage
            }}
            isExpanded={expandedMobilePositionId === position.id}
            onToggle={() => setExpandedMobilePositionId((current) => current === position.id ? null : position.id)}
            assetKind={assetKind}
            onDataChange={handleRefetch}
            planOptions={planOptions}
            initialPlanId={initialPlanId}
            refreshPlans={() => {
              try { refetchPlans?.(); } catch (_) {}
            }}
          />
        ))}
      </div>
      <div className="hidden md:block overflow-x-auto overflow-y-visible">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th 
                className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('token.name')}
              >
                Asset {getSortIndicator('token.name')}
              </th>
              <th 
                className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('currentPrice')}
              >
                Price {getSortIndicator('currentPrice')}
              </th>
              {!isStock && (
              <th 
                className="px-2 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer w-16"
                onClick={() => handleSort('token.marketData.priceChange1h')}
              >
                1h {getSortIndicator('token.marketData.priceChange1h')}
              </th>
              )}
              <th 
                className="px-2 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer w-16"
                onClick={() => handleSort('token.marketData.priceChange24h')}
              >
                {isStock ? 'Day' : '24h'} {getSortIndicator('token.marketData.priceChange24h')}
              </th>
              <th 
                className="px-2 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer w-16"
                onClick={() => handleSort('token.marketData.priceChange7d')}
              >
                7d {getSortIndicator('token.marketData.priceChange7d')}
              </th>
              <th 
                className="px-2 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer w-16"
                onClick={() => handleSort('token.marketData.priceChange30d')}
              >
                30d {getSortIndicator('token.marketData.priceChange30d')}
              </th>
              <th 
                className="px-2 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer w-16"
                onClick={() => handleSort('token.marketData.priceChange1y')}
              >
                1y {getSortIndicator('token.marketData.priceChange1y')}
              </th>
              {!isStock && (
              <>
              <th 
                className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer whitespace-nowrap"
                onClick={() => handleSort('token.marketData.volume24h')}
              >
                Vol-24h {getSortIndicator('token.marketData.volume24h')}
              </th>
              <th 
                className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('token.marketData.marketCap')}
              >
                MC {getSortIndicator('token.marketData.marketCap')}
              </th>
              <th 
                className="px-3 py-2 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer whitespace-nowrap"
                onClick={() => handleSort('riskScore')}
              >
                Risk {getSortIndicator('riskScore')}
              </th>
              </>
              )}
              <th 
                className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider min-w-[100px]"
              >
                Last 7D
              </th>
              <th 
                className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('holdingsValue')}
              >
                Holdings {getSortIndicator('holdingsValue')}
              </th>
              <th 
                className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort(groupHeader ? 'groupPercentage' : 'percentage')}
              >
                Size {getSortIndicator(groupHeader ? 'groupPercentage' : 'percentage')}
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                <button
                  type="button"
                  className="block w-full cursor-pointer text-right uppercase"
                  onClick={() => handleSort('currentPnl')}
                >
                  PnL $ {getSortIndicator('currentPnl')}
                </button>
                <button
                  type="button"
                  className="block w-full cursor-pointer text-right text-[10px] uppercase text-gray-500 dark:text-gray-400"
                  onClick={() => handleSort('pnlPercentage')}
                >
                  ROI % {getSortIndicator('pnlPercentage')}
                </button>
              </th>
              {!isStock && (
              <>
              <th 
                className="px-3 py-2 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider min-w-[130px] cursor-pointer"
                onClick={() => handleSort('strategyDelta')}
              >
                Strategy {getSortIndicator('strategyDelta')}
              </th>
              <th 
                className="px-3 py-2 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider"
              >
                Actions
              </th>
              </>
              )}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {positionsToRender.map((position) => (
              <PositionRow
                key={position.id}
                position={{
                  ...position,
                  // Use group-specific percentages when in grouped view
                  percentage: groupHeader ? position.groupPercentage : position.percentage,
                  cumulativePercentage: groupHeader ? position.groupCumulativePercentage : position.cumulativePercentage
                }}
                onDataChange={handleRefetch}
                planOptions={planOptions}
                initialPlanId={initialPlanId}
                refreshPlans={() => {
                  try { refetchPlans?.(); } catch (_) {}
                }}
                assetKind={assetKind}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
        <div className="flex items-start justify-between gap-2 lg:order-1 lg:shrink-0">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
              Positions
            </h1>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {totalCount === 0
                ? '0 positions'
                : totalPages <= 1
                  ? `${totalCount} positions`
                  : `${currentPage * pageSize + 1}-${Math.min((currentPage + 1) * pageSize, totalCount)} of ${totalCount}`}
            </div>
          </div>
        </div>
        <div className="order-2 flex items-center gap-2 sm:gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:order-3 lg:ml-auto lg:overflow-visible lg:pb-0">
          {!isStock && (
          <div className="flex shrink-0 items-center">
            <Tooltip 
              title={'Disabled because a group filter is applied'}
              position="bottom"
              trigger="mouseenter"
              interactive={false}
              arrow={true}
              theme="dark"
              size="small"
              disabled={!isGroupFilterApplied}
            >
              <label 
                htmlFor="grouped-view" 
                className={isGroupFilterApplied ? 'text-xs sm:text-sm mr-1.5 text-gray-400 dark:text-gray-500 whitespace-nowrap' : 'text-xs sm:text-sm mr-1.5 text-gray-700 dark:text-gray-300 whitespace-nowrap'}
              >
                Group View
              </label>
            </Tooltip>
            <Tooltip 
              title={'Disabled because a group filter is applied'}
              position="bottom"
              trigger="mouseenter"
              interactive={false}
              arrow={true}
              theme="dark"
              size="small"
              disabled={!isGroupFilterApplied}
            >
              <div className="relative inline-block w-11 mr-1 align-middle select-none">
                <input 
                  type="checkbox" 
                  name="grouped-view" 
                  id="grouped-view" 
                  className={
                    'toggle-checkbox absolute left-0.5 top-0.5 block w-5 h-5 rounded-full bg-white border-0 appearance-none transition-transform duration-200 ease-in ' +
                    (isGroupFilterApplied ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')
                  }
                  checked={groupedView}
                  onChange={(e) => setGroupedView(e.target.checked)}
                  disabled={isGroupFilterApplied}
                />
                <label 
                  htmlFor="grouped-view" 
                  className={
                    'toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 dark:bg-gray-600 ' +
                    (isGroupFilterApplied ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')
                  }
                ></label>
              </div>
            </Tooltip>
          </div>
          )}
          {!isStock && <div className="flex shrink-0 items-center">
            <Tooltip 
              title={`Hide positions with holdings < $${DUST_THRESHOLD_USD}`}
              position="bottom"
              trigger="mouseenter"
              interactive={false}
              arrow={true}
              theme="dark"
              size="small"
            >
              <label 
                htmlFor="hide-dust" 
                className="text-xs sm:text-sm mr-1.5 text-gray-700 dark:text-gray-300 whitespace-nowrap"
              >
                Hide Dust
              </label>
            </Tooltip>
            <Tooltip 
              title={`Hide positions with holdings < $${DUST_THRESHOLD_USD}`}
              position="bottom"
              trigger="mouseenter"
              interactive={false}
              arrow={true}
              theme="dark"
              size="small"
            >
              <div className="relative inline-block w-11 mr-1 align-middle select-none">
                <input 
                  type="checkbox" 
                  name="hide-dust" 
                  id="hide-dust" 
                  className="toggle-checkbox absolute left-0.5 top-0.5 block w-5 h-5 rounded-full bg-white border-0 appearance-none cursor-pointer transition-transform duration-200 ease-in"
                  checked={hideDust}
                  onChange={(e) => setHideDust(e.target.checked)}
                />
                <label 
                  htmlFor="hide-dust" 
                  className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 dark:bg-gray-600 cursor-pointer"
                ></label>
              </div>
            </Tooltip>
          </div>}
          {(() => {
            const appliedCount = [
              selectedGroup ? 1 : 0,
              selectedLocation ? 1 : 0,
              selectedMarketCap ? 1 : 0,
              isStock && searchTerm.trim() ? 1 : 0,
              isStock && hideDust ? 1 : 0,
              isStock && selectedSector ? 1 : 0,
              isStock && selectedInstrumentType ? 1 : 0,
              exitStrategyFilter ? 1 : 0,
              invalidationFilter ? 1 : 0,
              selectedTags.length > 0 ? 1 : 0
            ].reduce((a, b) => a + b, 0);
            const label = appliedCount > 0 ? `Filter (${appliedCount})` : 'Filter';
            return (
              <div className="ml-auto flex shrink-0 items-center gap-2 lg:ml-1">
                {isFiltered && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline"
                    title="Clear all filters"
                  >
                    Clear
                  </button>
                )}
                <button
                  onClick={() => setIsFilterModalOpen(true)}
                  className="inline-flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
                >
                  {label}
                </button>
              </div>
            );
          })()}
        </div>
        <div className="order-3 flex w-full items-center gap-2 lg:order-2 lg:w-auto">
          <input
            type="text"
            placeholder="Search name or ticker"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="min-w-0 flex-1 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm lg:w-72 lg:flex-none xl:w-96 lg:py-1.5"
          />
          <div className="flex shrink-0 items-center gap-1 md:hidden" aria-label="Mobile position sorting">
            <select
              aria-label="Sort positions by"
              value={sortBy}
              onChange={(e) => handleMobileSortByChange(e.target.value)}
              className="max-w-[6.5rem] rounded-md border border-gray-300 bg-white px-2 py-2 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            >
              {(isStock ? STOCK_MOBILE_SORT_OPTIONS : MOBILE_SORT_OPTIONS).map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <select
              aria-label="Sort direction"
              value={sortDir}
              onChange={(e) => setSortDir(e.target.value)}
              className="rounded-md border border-gray-300 bg-white px-2 py-2 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="desc">↓</option>
              <option value="asc">↑</option>
            </select>
          </div>
        </div>
      </div>

      <PositionFilterModal
        isOpen={isFilterModalOpen}
        assetKind={assetKind}
        stockSectorOptions={stockSectorOptions}
        stockTypeOptions={stockTypeOptions}
        initialSearchTerm={searchTerm}
        initialHideDust={hideDust}
        initialSelectedSector={selectedSector}
        initialSelectedInstrumentType={selectedInstrumentType}
        onClose={() => setIsFilterModalOpen(false)}
        tokenGroups={groupsData?.tokenGroups || []}
        tokenLocations={tokenLocations}
        initialSelectedGroup={selectedGroup}
        initialSelectedLocation={selectedLocation}
        initialSelectedMarketCap={selectedMarketCap}
        initialExitStrategyFilter={exitStrategyFilter}
        initialInvalidationFilter={invalidationFilter}
        initialSelectedTags={selectedTags}
        initialTagFilterMode={tagFilterMode}
        onApply={({ searchTerm: search, hideDust: dust, selectedSector: sector, selectedInstrumentType: type, selectedGroup: g, selectedLocation: l, selectedMarketCap: mc, exitStrategyFilter: ef, invalidationFilter: inv, selectedTags: tags, tagFilterMode: tfm }) => {
          if (isStock) {
            setSearchTerm(search ?? '');
            setHideDust(dust ?? false);
          }
          setSelectedSector(isStock ? sector ?? null : null);
          setSelectedInstrumentType(isStock ? type ?? null : null);
          setSelectedGroup(g);
          setSelectedLocation(l);
          setSelectedMarketCap(mc);
          setExitStrategyFilter(ef ?? '');
          setInvalidationFilter(inv ?? '');
          setSelectedTags(tags ?? []);
          setTagFilterMode(tfm ?? 'ANY');
          if (g) setGroupedView(false);
        }}
        onReset={resetFilters}
      />

      {error && (
        <div className="mb-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-200 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error.message}</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center min-h-[280px] bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : hasNoPositions && isStock && !isFiltered ? (
        <EmptyState
          title="No stock positions yet"
          description="Record a buy on an instrument page to see it here."
          action={() => window.dispatchEvent(new Event('open-search'))}
          actionLabel="Search instruments"
        />
      ) : hasNoPositions ? (
        <EmptyState
          title="No positions found"
          description={
            (selectedGroup || selectedLocation || selectedMarketCap || (debouncedSearchTerm && debouncedSearchTerm.trim()) || exitStrategyFilter || invalidationFilter)
              ? 'Try adjusting your filters or clear them to see all positions.'
              : 'Add some positions to get started!'
          }
          action={isFiltered ? resetFilters : null}
          actionLabel={isFiltered ? 'Clear filters' : null}
        />
      ) : !isStock && groupedView && !selectedGroup ? (
        // If grouped view is enabled and no specific group is selected
        <div>
          {groups.map(group => (
            <div key={group.id} className="mb-6">
              {renderPositionTable(group.positions, group)}
            </div>
          ))}
        </div>
      ) : (
        // Default view or when a specific group is selected
        renderPositionTable(sortedPositionsWithCumulative)
      )}

      {totalCount > 0 && (
        <div className="mt-2 flex flex-wrap items-center justify-end gap-3 py-3">
          <span className="text-sm text-gray-600 dark:text-gray-400">Per page:</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          {totalPages > 1 && (
            <>
              <button
                type="button"
                disabled={currentPage <= 0}
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Page {currentPage + 1} of {totalPages}
              </span>
              <button
                type="button"
                disabled={currentPage >= totalPages - 1}
                onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PositionList;
